import React, { type ReactNode } from 'react'
import { ButtonGroup, Dropdown } from 'react-bootstrap'
import type { Asset } from '@ztechapps/common.contract'
import { AssetTypeEnum } from '@ztechapps/common.contract'
import { useToast } from '@ztechapps/common.hook'
import type { DragHandleContentProps } from '@ztechapps/webui.util'
import { DragHandle, ShowIf } from '@ztechapps/webui.util'
import { BusyButton } from '@ztechapps/webui.button'
import { useCopyToClipboard } from 'react-use'
import { useAssetPageContext } from '../Context'
import { DeleteAssetButton } from '../DeleteAssetButton'
import { useHandleProps } from '../../Table/HandlePropsContext'

export const DragHandleButton = (props: DragHandleContentProps) => {
  return <BusyButton bsSize="sm" {...props} />
}

const CustomToggle = React.forwardRef(({ onClick }: { children: ReactNode; onClick: (e: unknown) => void }, _ref) => {
  return <BusyButton onClick={onClick} icon="copy" bsSize="sm" title="Copy URL" />
})

export const CopyUrlDropdown = ({ asset }: { asset: Asset }) => {
  const [, copyToClipboard] = useCopyToClipboard()
  const { showInfo } = useToast()

  const isImageAsset = asset?.type === AssetTypeEnum.image
  const originalUrl = asset?.url
  const mediumUrl = asset.mediumUrl || originalUrl
  const thumbUrl = asset?.thumbUrl || mediumUrl
  const largeUrl = asset?.largeUrl || originalUrl

  const onCopyUrl = (url: string) => {
    copyToClipboard(url)
    showInfo({ message: `${url} is ready to be pasted.` })
  }

  return (
    <Dropdown>
      <Dropdown.Toggle as={CustomToggle} id="dropdown-custom-url">
        Copy
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item eventKey="4" onClick={() => onCopyUrl(originalUrl)}>
          {isImageAsset ? 'Copy Original Size URL' : 'Copy URL'}
        </Dropdown.Item>
        <ShowIf condition={isImageAsset}>
          <Dropdown.Divider />
          <Dropdown.Item eventKey="1" onClick={() => onCopyUrl(thumbUrl)}>
            Copy Thumbnail URL
          </Dropdown.Item>
          <Dropdown.Item eventKey="2" onClick={() => onCopyUrl(mediumUrl)}>
            Copy Medium Size URL
          </Dropdown.Item>
          <Dropdown.Item eventKey="3" onClick={() => onCopyUrl(largeUrl)}>
            Copy Large Size URL
          </Dropdown.Item>
        </ShowIf>
      </Dropdown.Menu>
    </Dropdown>
  )
}

export const OpenFileButton = ({ url }: { url: string }) => {
  const openInNewTab = () => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return <BusyButton onClick={openInNewTab} icon="arrow-up-right-from-square" bsSize="sm" title="Open file externally" />
}

type AssetActionButtonsProps = {
  asset: Asset
}

export const AssetActionButtons = ({ asset }: AssetActionButtonsProps) => {
  const { canEdit } = useAssetPageContext()
  const handleProps = useHandleProps()

  return (
    <ButtonGroup>
      <DragHandle disabled={!canEdit} title="Drag to sort the assets" As={DragHandleButton} {...handleProps} />
      <CopyUrlDropdown asset={asset} />
      <OpenFileButton url={asset?.url} />
      <DeleteAssetButton _id={asset?._id} />
    </ButtonGroup>
  )
}
